(() => {
  const WRITE_TIMEOUT_MS = 2500;
  const MAX_RETRIES      = 3;
  const RETRY_BASE_MS    = 500;
  const POLL_MAX_MS      = 5000;

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  async function getCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  function safeJSON(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  function readEnroller() {
    const x = safeJSON(localStorage.getItem('enrollerinfo'));
    if (!x || typeof x !== 'object') return null;

    const first = (x.ContactFirstName || '').trim();
    const last  = (x.ContactLastName  || '').trim();
    const name  = `${first} ${last}`.trim();

    let id = '';
    if (x.AssociateID != null && String(x.AssociateID).trim() !== '') {
      id = String(x.AssociateID).trim();
    } else if (x.WebAlias != null && String(x.WebAlias).trim() !== '') {
      id = String(x.WebAlias).trim();
    } else if (x.BackOfficeID != null && String(x.BackOfficeID).trim() !== '') {
      id = String(x.BackOfficeID).trim();
    }

    if (!id) return null;
    return { name, id };
  }

  const getCookie = (name) => {
    const esc = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const m = document.cookie.match(new RegExp('(^|; )' + esc + '=([^;]*)'));
    return m ? decodeURIComponent(m[2]) : null;
  };

  function readAttributionExtras() {
    return {
      existingReferral:     getCookie('existingReferral') || 'None',
      landingPage:          getCookie('landingPage') || 'None',
      landingPageSession:   sessionStorage.getItem('landingPage') || 'None',
      sponsorSearch:        String(!!sessionStorage.getItem('sponsorSearch')) || 'false',
    };
  }

  async function postJSONWithTimeout(url, body, timeoutMs, retries) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          signal: ctrl.signal,
          body: JSON.stringify(body),
        });
        clearTimeout(t);
        if (r.ok) return true;
        if (r.status >= 400 && r.status < 500) return false;
      } catch {
        // network error -> backoff
      } finally {
        clearTimeout(t);
      }
      await sleep(RETRY_BASE_MS * Math.pow(2, attempt));
    }
    return false;
  }

  function sendBeaconCartAttributes(attrs) {
    try {
      if (!navigator.sendBeacon) return false;
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(attrs)) {
        params.append(`attributes[${k}]`, v == null ? '' : String(v));
      }
      const blob = new Blob([params.toString()], {
        type: 'application/x-www-form-urlencoded;charset=UTF-8'
      });
      return navigator.sendBeacon('/cart/update.js', blob);
    } catch {
      return false;
    }
  }

  function attributesMatch(existingObj, desiredObj) {
    if (!existingObj) return false;
    for (const [k, v] of Object.entries(desiredObj)) {
      const ev = existingObj[k];
      if (String(ev ?? '') !== String(v ?? '')) return false;
    }
    return true;
  }

  async function buildDesiredAttributes() {
    const cart = await getCart();
    if (!cart || !cart.item_count || cart.item_count < 1) return null;

    const enroller = readEnroller();
    if (!enroller) return null;

    const extras = readAttributionExtras();
    return {
      ConsultantName: enroller.name || '',
      ConsultantID:   enroller.id   || '',
      existingReferral:      extras.existingReferral,
      landingPage:           extras.landingPage,
      landingPageSession:    extras.landingPageSession,
      sponsorSearch:         extras.sponsorSearch,
    };
  }

  let inFlight = false;
  async function writeAttributesIfEligible({ allowPoll = true } = {}) {
    if (inFlight) return;
    inFlight = true;
    try {
      const start = Date.now();
      while (true) {
        const attrs = await buildDesiredAttributes();
        if (attrs) {
          const cart = await getCart();
          if (!cart) return;
          const existing = cart.attributes || {};
          if (attributesMatch(existing, attrs)) return;

          const ok = await postJSONWithTimeout('/cart/update.js', { attributes: attrs }, WRITE_TIMEOUT_MS, MAX_RETRIES);
          if (ok) return;

          const beaconOK = sendBeaconCartAttributes(attrs);
          if (beaconOK) return;
        }

        if (!allowPoll) return;
        if (Date.now() - start >= POLL_MAX_MS) return;
        await sleep(400);
      }
    } finally {
      inFlight = false;
    }
  }

  writeAttributesIfEligible({ allowPoll: true }).catch(() => {});

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      (async () => {
        const attrs = await buildDesiredAttributes();
        if (attrs) sendBeaconCartAttributes(attrs);
      })();
    }
  }, { passive: true });

  addEventListener('pagehide', () => {
    (async () => {
      const attrs = await buildDesiredAttributes();
      if (attrs) sendBeaconCartAttributes(attrs);
    })();
  }, { passive: true });

  addEventListener('pageshow', (e) => {
    if (e.persisted) writeAttributesIfEligible({ allowPoll: true }).catch(() => {});
  });

  setTimeout(() => {
    writeAttributesIfEligible({ allowPoll: true }).catch(() => {});
  }, 800);

})();
